import React, { Fragment, useState, useEffect, useRef } from "react";
import { Dropdown, Modal, Button } from "react-bootstrap";
import { db, functions } from "../../../firebase";
import {
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  doc,
  collection,
  deleteField,
} from "firebase/firestore";
import swal from "sweetalert";
import { notifyError, notifyTopRight } from "../toast/SetToast";
import { useAuth } from "../../../context/AuthContext";
import { httpsCallable } from "firebase/functions";
import AdminNodeSelect, { normalizeAdminNodeCodes } from "./AdminNodeSelect";
import AdminCustomerAccessSelect from "./AdminCustomerAccessSelect";
import PatientAccountHnLink from "./PatientAccountHnLink";
import {
  buildCustomerAccessField,
  createCustomerAccessDraft,
} from "../../../utils/staffCustomerAccess";
import { filterAdminAccounts, paginateAdminAccounts } from "./AdminAccountFilters";


const ACCOUNT_ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
  { value: "sale", label: "Sale" },
  { value: "accountant", label: "Accountant" },
  { value: "sleep_physician", label: "Sleep Physician" },
  { value: "sleep_technician", label: "Sleep Technician" },
];

const List = ({ nodesList, refreshKey = 0 }) => {
  const deleteUserByEmail = httpsCallable(functions, "deleteUserByEmail");
  const updateAccountRole = httpsCallable(functions, "updateAccountRole");
  const { currentUser } = useAuth();
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [adminsList, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editNodeCodes, setEditNodeCodes] = useState([]);
  const [editCustomerAccessDraft, setEditCustomerAccessDraft] = useState({});
  const [editRole, setEditRole] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [accountType, setAccountType] = useState("");
  const [patientAccounts, setPatientAccounts] = useState([]);
  const [patientDetail, setPatientDetail] = useState(null);
  const [hnLinkBusy, setHnLinkBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // const [goNext, setGoNext] = useState(false);
  // const [goPrev, setGoPrev] = useState(true);
  // const [page, setPage] = useState(1);
  // const [totalPages, setTotalPages] = useState(1);
  // const itemsPerPage = 10;

  const firstnameRef = useRef();
  const lastnameRef = useRef();
  // const positionRef = useRef();
  const adminDefault = {
    firstname: "",
    lastname: "",
    // email: "",
    node: "",
    // position: "",
    role: "",
    // photoURL: "",
  };
  const tmpAdmin = {
    doc: adminDefault,
    id: "",
  };
  const [currentAdmin, setCurrentAdmin] = useState(tmpAdmin);
  const getNodeDisplayLabel = (nodeCode) => {
    const normalizedCode = String(nodeCode || "").trim();
    const node = (nodesList || []).find(
      (item) => String(item.nodecode || "").trim() === normalizedCode
    );

    return node && node.nodename
      ? `${node.nodename} (${normalizedCode})`
      : normalizedCode;
  };
  const visiblePatients = currentUser && (currentUser.roles || []).includes("super_admin") ? patientAccounts : [];
  const patientEmails = new Set(visiblePatients.map((row) => String(row.email || "").toLowerCase()).filter(Boolean));
  const accounts = adminsList.filter((row) => !patientEmails.has(String(row.email || "").toLowerCase())).concat(visiblePatients)
    .sort((a, b) => String(a.displayName || a.email || a.phone || "").localeCompare(String(b.displayName || b.email || b.phone || "")));
  const filteredAccounts = filterAdminAccounts(accounts, searchTerm, roleFilter, nodesList, accountType);
  const pagination = paginateAdminAccounts(filteredAccounts, page, pageSize);
  useEffect(() => { setPage(1); }, [searchTerm, roleFilter, accountType, pageSize]);
  useEffect(() => { if (page !== pagination.page) setPage(pagination.page); }, [page, pagination.page]);

  const openEditAdmin = (adminInfo) => {
    const nodeCodes = normalizeAdminNodeCodes(adminInfo.node);
    const currentRole =
      adminInfo.role ||
      (Array.isArray(adminInfo.roles) ? adminInfo.roles[0] : "");
    setCurrentAdmin(adminInfo);
    setEditNodeCodes(nodeCodes);
    setEditRole(currentRole);
    setEditCustomerAccessDraft(
      createCustomerAccessDraft(adminInfo, nodeCodes)
    );
    setEditModal(true);
  };

  const closeEditAdmin = () => {
    setEditModal(false);
    setEditNodeCodes([]);
    setEditCustomerAccessDraft({});
    setEditRole("");
  };

  const isSuperAdmin =
    currentUser &&
    Array.isArray(currentUser.roles) &&
    currentUser.roles.includes("super_admin");
  const isEditingOwnAccount =
    currentUser &&
    currentAdmin.email &&
    String(currentUser.email || "").toLowerCase() ===
      String(currentAdmin.email).toLowerCase();

  const drop = (adminInfo) => {
    return (
      <Dropdown>
        <Dropdown.Toggle variant="" className="table-dropdown icon-false">
          <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
              <rect x="0" y="0" width="24" height="24"></rect>
              <circle fill="#000000" cx="5" cy="12" r="2"></circle>
              <circle fill="#000000" cx="12" cy="12" r="2"></circle>
              <circle fill="#000000" cx="19" cy="12" r="2"></circle>
            </g>
          </svg>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() => openEditAdmin(adminInfo)}
          >
            Admin Detail
          </Dropdown.Item>

          {(currentUser.roles || []).some((role) => ["super_admin", "admin"].includes(role)) && (
            <Dropdown.Item
              disabled={isDeleting || String(adminInfo.email || "").toLowerCase() === String(currentUser.email || "").toLowerCase()}
              onClick={() => refDeleteDoc(adminInfo)}
              className="text-danger"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Dropdown.Item>
          )}
          {/* <Dropdown.Item
            href="#"
            onClick={() => {
              // refDeleteDoc(bookingInfo);
              swal({
                title: "Are you sure to delete ?",
                text: "Once deleted, you will not be able to recover this account !",
                icon: "warning",
                buttons: true,
                dangerMode: true,
              }).then((willDelete) => {
                if (willDelete) {
                  // console.log(adminInfo);
                  refDeleteDoc(adminInfo);
                }
              });
            }}
            className="text-danger"
          >
            Delete
          </Dropdown.Item> */}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const chackbox = document.querySelectorAll(".customer_shop_single input");
  const motherChackBox = document.querySelector(".customer_shop input");
  // console.log(document.querySelectorAll(".publish_review input")[0].checked);
  const chackboxFun = (type) => {
    for (let i = 0; i < chackbox.length; i++) {
      const element = chackbox[i];
      if (type === "all") {
        if (motherChackBox.checked) {
          element.checked = true;
        } else {
          element.checked = false;
        }
      } else {
        if (!element.checked) {
          motherChackBox.checked = false;
          break;
        } else {
          motherChackBox.checked = true;
        }
      }
    }
  };

  const chack = (i) => (
    <div className={`custom-control custom-checkbox ml-2`}>
      <input
        type="checkbox"
        className="custom-control-input "
        id={`checkAll${i}`}
        required=""
        onClick={() => chackboxFun()}
      />
      <label className="custom-control-label" htmlFor={`checkAll${i}`}></label>
    </div>
  );

  const editmodal = (
    <Modal
      className="fade bd-example-modal-xl"
      show={editModal}
      onHide={closeEditAdmin}
      size="xl"
    >
      <Modal.Header>
        <Modal.Title>Edit Admin</Modal.Title>
        <Button
          variant=""
          className="close"
          onClick={closeEditAdmin}
        >
          <span>&times;</span>
        </Button>
      </Modal.Header>
      <Modal.Body>
        <div className="card-body">
          <div className="row">
            <div className="col-xl-11">
              <div className="form-group row">
                <label
                  className="col-lg-3 col-form-label"
                  htmlFor="val-username"
                >
                  Display Name
                </label>
                <div className="col-lg-4">
                  <text>
                    <b>{currentAdmin.displayName}</b>
                  </text>
                </div>
              </div>
              <div className="form-group row">
                <label
                  className="col-lg-3 col-form-label"
                  htmlFor="val-username"
                >
                  Name
                </label>
                <div className="col-lg-4">
                  <input
                    type="text"
                    className="form-control"
                    id="val-username"
                    name="val-username"
                    placeholder="First Name"
                    // value=""
                    ref={firstnameRef}
                    defaultValue={currentAdmin.firstname}
                  />
                </div>
                <div className="col-lg-4">
                  <input
                    type="text"
                    className="form-control"
                    id="val-username"
                    name="val-username"
                    placeholder="Last Name"
                    ref={lastnameRef}
                    defaultValue={currentAdmin.lastname}
                  />
                </div>
              </div>
              <div className="form-group row">
                <label
                  className="col-lg-3 col-form-label"
                  htmlFor="val-username"
                >
                  Email
                </label>
                <div className="col-lg-4 mt-2">
                  <text>
                    <b>{currentAdmin.email}</b>
                  </text>
                </div>
              </div>

              <div className="form-group row">
                <label
                  className="col-lg-3 col-form-label"
                  htmlFor="edit-admin-nodes"
                >
                  Node
                </label>
                <div className="col-lg-8 mt-2">
                  <AdminNodeSelect
                    inputId="edit-admin-nodes"
                    nodesList={nodesList}
                    value={editNodeCodes}
                    onChange={setEditNodeCodes}
                  />
                </div>
              </div>
              {["staff", "admin"].includes(editRole) && (
                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">
                    Customer Access
                  </label>
                  <div className="col-lg-8 mt-2">
                    <AdminCustomerAccessSelect
                      nodesList={nodesList}
                      selectedNodeCodes={editNodeCodes}
                      value={editCustomerAccessDraft}
                      onChange={setEditCustomerAccessDraft}
                    />
                  </div>
                </div>
              )}
              <div className="form-group row">
                <label
                  className="col-lg-3 col-form-label"
                  htmlFor="val-username"
                >
                  Role
                </label>
                <div className="col-lg-4 mt-2">
                  {isSuperAdmin && !isEditingOwnAccount ? (
                    <select
                      className="form-control"
                      value={editRole}
                      onChange={(event) => setEditRole(event.target.value)}
                    >
                      {ACCOUNT_ROLE_OPTIONS.map((roleOption) => (
                        <option
                          key={roleOption.value}
                          value={roleOption.value}
                        >
                          {roleOption.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <text>
                      <b>{editRole}</b>
                    </text>
                  )}
                  {isSuperAdmin && isEditingOwnAccount && (
                    <small className="form-text text-muted">
                      You cannot change your own role.
                    </small>
                  )}
                </div>
              </div>

              <div className="form-group row mt-5">
                <label className="col-lg-3 col-form-label"> </label>
                <div className="col-lg-4"> </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger light" onClick={closeEditAdmin}>
          Cancel
        </Button>
        <Button
          variant=""
          type="button"
          onClick={() => handleEdit()}
          className="btn btn-primary"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Edit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const refDeleteDoc = async (adminInfo) => {
    if (isDeleting) return;
    const confirmed = await swal({
      title: "Delete account?",
      text: `Delete the login and account profile for ${adminInfo.email}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      const result = await deleteUserByEmail({
        email: adminInfo.email,
        adminDocumentId: adminInfo.documentId || adminInfo.displayName,
      });
      if (!result.data || result.data.success !== true) {
        throw new Error("Account deletion was not confirmed. Refresh and retry; the email may still be registered.");
      }
      setAdmins((items) => items.filter((item) => item.documentId !== adminInfo.documentId));
      await swal("Account has been deleted. The email can be used again.", { icon: "success" });
      await getAdminsList();
    } catch (error) {
      notifyError(error.message || "Account deletion failed. Please retry.");
    } finally {
      setIsDeleting(false);
    }
  };
  // const handleEdit = async () => {
  //   const docRef = doc(db, "admins", currentAdmin.id);

  //   let update = {
  //     ...currentAdmin,
  //   };
  //   update.firstname = firstnameRef.current.value;
  //   update.lastname = lastnameRef.current.value;

  //   await updateDoc(docRef, {
  //     ...update,
  //   });

  //   notifyTopRight("Update success");
  //   setEditModal(false);
  //   getAdminsList();
  //   return;
  // };

  const handleEdit = async () => {
    const docRef = doc(
      db,
      "admins",
      currentAdmin.documentId || currentAdmin.displayName
    );

    const originalRole =
      currentAdmin.role ||
      (Array.isArray(currentAdmin.roles) ? currentAdmin.roles[0] : "");

    if (
      isSuperAdmin &&
      !isEditingOwnAccount &&
      editRole !== originalRole
    ) {
      const shouldChangeRole = await swal({
        title: "Change account role?",
        text: `Change ${currentAdmin.email} from ${originalRole} to ${editRole}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });
      if (!shouldChangeRole) return;
    }

    let update = {
      firstname: firstnameRef.current.value,
      lastname: lastnameRef.current.value,
      // The UI displays full names, but Firestore keeps the existing code array.
      node: [...editNodeCodes],
    };

    if (["staff", "admin"].includes(editRole)) {
      const customerAccess = buildCustomerAccessField(
        editCustomerAccessDraft,
        editNodeCodes
      );
      update.customerAccess =
        customerAccess === null ? deleteField() : customerAccess;
    } else if (["staff", "admin"].includes(originalRole)) {
      update.customerAccess = deleteField();
    }

    setIsSaving(true);
    try {
      let roleChanged = false;
      if (isSuperAdmin && !isEditingOwnAccount) {
        const roleResponse = await updateAccountRole({
          email: currentAdmin.email,
          role: editRole,
          adminDocumentId:
            currentAdmin.documentId || currentAdmin.displayName,
        });
        roleChanged = Boolean(roleResponse.data && roleResponse.data.changed);
      }

      await updateDoc(docRef, update);

      notifyTopRight(
        roleChanged
          ? "Update success. The user must sign out and sign in again."
          : "Update success"
      );
      closeEditAdmin();
      await getAdminsList();
    } catch (error) {
      notifyError(error.message || "Unable to update this account");
    } finally {
      setIsSaving(false);
    }
  };

  const getAdminsList = async () => {
    let admins = [];
    const docSnap = await getDocs(collection(db, "admins"));
    docSnap.forEach((adminDoc) => {
      const adminData = adminDoc.data() || {};
      admins.push({
        ...adminData,
        documentId: adminDoc.id,
        displayName: adminData.displayName || adminDoc.id,
      });
    });
    if ((currentUser.roles || []).includes("super_admin")) {
      setAdmins(admins);
    } else {
      const currentAdmin = await getDoc(
        doc(db, "admins", currentUser.displayName)
      );
      const filterAdmins = admins.filter((admin) => {
        return admin.node == currentAdmin.data().node;
      });
      // console.log(adminsFilter)
      setAdmins(filterAdmins);
    }
  };
  useEffect(() => {
    let active = true;
    setIsLoading(true); setLoadError(""); setPatientAccounts([]);
    const load = async () => {
      await getAdminsList();
      if (!isSuperAdmin || !active) return;
      const listPatients = httpsCallable(functions, "listPatientAccounts", { timeout: 120000 });
      const patients = [];
      const seen = new Set();
      let cursor = null;
      do {
        if (!active) return;
        const response = await listPatients({ cursor });
        patients.push(...response.data.accounts);
        cursor = response.data.cursor;
        if (cursor && seen.has(cursor)) throw new Error("Account pagination did not advance.");
        if (cursor) seen.add(cursor);
      } while (cursor);
      if (active) setPatientAccounts(patients);
    };
    load().catch(() => { if (active) setLoadError("Unable to load all accounts. Use Refresh to retry."); })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [currentUser.uid, isSuperAdmin, refreshKey, reload]);
  return (
    <Fragment>
      <div className="row"><div className="col-lg-12"><div className="card"><div className="card-body">
        {loadError && <div className="alert alert-danger" role="alert">{loadError}</div>}
        <div className="row align-items-end mb-3">
          <div className="col-lg-5 col-md-6 mb-2">
            <label htmlFor="admin-account-search">Search accounts</label>
            <input id="admin-account-search" type="search" className="form-control" placeholder="Name, display name, email, phone, HN, node or position" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
          </div>
          <div className="col-lg-2 col-md-3 mb-2">
            <label htmlFor="account-type-filter">Account type</label>
            <select id="account-type-filter" className="form-control" value={accountType} onChange={(event) => setAccountType(event.target.value)}>
              <option value="">All accounts</option><option value="staff">Staff accounts</option><option value="patient">Patient accounts</option>
            </select>
          </div>
          <div className="col-lg-2 col-md-3 mb-2">
            <label htmlFor="admin-account-role-filter">Role</label>
            <select id="admin-account-role-filter" className="form-control" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
              <option value="">All Roles</option>
              {ACCOUNT_ROLE_OPTIONS.concat([{ value: "patient", label: "Patient" }]).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div className="col-lg-3 col-md-12 mb-2 d-flex flex-wrap">
            <Button size="sm" variant="outline-secondary" className="mr-2" onClick={() => { setSearchTerm(""); setRoleFilter(""); setAccountType(""); setPage(1); }}>Clear filters</Button>
            <Button size="sm" disabled={isLoading} onClick={() => setReload((value) => value + 1)}>{isLoading ? "Loading…" : "Refresh"}</Button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table mb-0 table-striped">
            <thead><tr><th>Name / Display name</th><th>Email / Phone</th><th>HN</th><th>Node</th><th>Role</th><th>Options</th></tr></thead>
            <tbody>
              {pagination.rows.map((account) => {
                const patient = account.accountType === "patient";
                const name = [account.firstname, account.lastname].filter(Boolean).join(" ") || account.displayName || "No name";
                return <tr key={account.documentId || account.uid || account.email}>
                  <td><button type="button" className="btn btn-link p-0 text-left" onClick={() => patient ? setPatientDetail(account) : openEditAdmin(account)}>{name}</button>{account.displayName && account.displayName !== name && <small className="d-block text-muted">{account.displayName}</small>}{patient && <small className="d-block text-muted">{account.source}</small>}</td>
                  <td style={{ overflowWrap: "anywhere" }}>{account.email || account.phone || "—"}{account.email && account.phone && <small className="d-block">{account.phone}</small>}</td>
                  <td>{patient ? account.hn || "Unlinked" : "—"}</td>
                  <td>{patient ? "—" : normalizeAdminNodeCodes(account.node).map((node) => <span key={node} className="badge badge-primary mr-1">{getNodeDisplayLabel(node)}</span>)}</td>
                  <td>{account.role || (account.roles || [])[0]}{patient && <><small className={`d-block ${account.disabled ? "text-danger" : "text-muted"}`}>{account.disabled ? "Disabled" : "Enabled"}</small><small className="d-block text-muted">{account.email ? account.emailVerified ? "Email verified" : "Email verification pending" : "Phone account"}</small></>}</td>
                  <td>{patient ? <Button size="sm" variant="outline-primary" onClick={() => setPatientDetail(account)}>Patient detail</Button> : drop(account)}</td>
                </tr>;
              })}
              {!pagination.rows.length && <tr><td colSpan={6} className="text-center py-4 text-muted">{isLoading ? "Loading accounts…" : "No accounts match the selected filters."}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="d-flex flex-wrap align-items-center justify-content-between mt-3">
          <span className="text-muted small mb-2">Showing {pagination.start}–{pagination.end} of {filteredAccounts.length} accounts</span>
          <div className="d-flex align-items-center mb-2">
            <label className="small mb-0 mr-2" htmlFor="account-page-size">Rows</label>
            <select id="account-page-size" className="form-control form-control-sm mr-3" style={{ width: 75 }} value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>{[10, 25, 50].map((size) => <option key={size} value={size}>{size}</option>)}</select>
            <Button size="sm" variant="outline-secondary" disabled={pagination.page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
            <span className="small mx-3">Page {pagination.page} of {pagination.totalPages}</span>
            <Button size="sm" variant="outline-secondary" disabled={pagination.page >= pagination.totalPages} onClick={() => setPage((value) => value + 1)}>Next</Button>
          </div>
        </div>
      </div></div></div></div>
      {editmodal}
      <Modal show={Boolean(patientDetail)} onHide={() => { if (!hnLinkBusy) setPatientDetail(null); }} centered>
        <Modal.Header closeButton={!hnLinkBusy}><Modal.Title>Patient account</Modal.Title></Modal.Header>
        <Modal.Body>{patientDetail && <><dl className="row mb-0">
          <dt className="col-sm-4">Name</dt><dd className="col-sm-8">{[patientDetail.firstname, patientDetail.lastname].filter(Boolean).join(" ") || patientDetail.displayName || "—"}</dd>
          <dt className="col-sm-4">Email</dt><dd className="col-sm-8 text-break">{patientDetail.email || "—"}</dd>
          <dt className="col-sm-4">Phone</dt><dd className="col-sm-8">{patientDetail.phone || "—"}</dd>
          <dt className="col-sm-4">HN</dt><dd className="col-sm-8">{patientDetail.hn || "Unlinked"}</dd>
          <dt className="col-sm-4">Account source</dt><dd className="col-sm-8">{patientDetail.source}</dd>
          <dt className="col-sm-4">Status</dt><dd className="col-sm-8">{patientDetail.disabled ? "Disabled" : "Enabled"}</dd>
          <dt className="col-sm-4">Created</dt><dd className="col-sm-8">{patientDetail.createdAt ? new Date(patientDetail.createdAt).toLocaleString() : "—"}</dd>
          <dt className="col-sm-4">Last sign-in</dt><dd className="col-sm-8">{patientDetail.lastSignInAt ? new Date(patientDetail.lastSignInAt).toLocaleString() : "Never"}</dd>
        </dl><PatientAccountHnLink key={patientDetail.uid} account={patientDetail} allowed={isSuperAdmin} onBusyChange={setHnLinkBusy} onLinked={(hn) => { setPatientAccounts((rows) => rows.map((account) => account.uid === patientDetail.uid ? { ...account, hn } : account)); setPatientDetail((account) => ({ ...account, hn })); }} /></>}</Modal.Body>
        <Modal.Footer><Button variant="light" disabled={hnLinkBusy} onClick={() => setPatientDetail(null)}>Close</Button></Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default List;
